import React from "react";
import { useNavigate } from "react-router-dom";
import AssignRateSheet from "~/visuals/organisms/AssignRateSheet";
import {
  project as projectRoute,
  customer as customerRoute,
  customerManagement,
} from "~/routes";
import { useBreadcrumbs } from "~/main-layout/BreadcrumbProvider";
import { Moment } from "moment";
import Spinner from "~/components/spinner";
import { useReloadProject } from "~/visuals/pages/Project/ProjectRouter";
import { useAssignExpenseRateSheetToProjectMutation } from "./query.generated";
import { ValueRenderer } from "~/components/forms/ValueContext";
import { ValidationResponse } from "~/gql/types";
import { Project } from "~/gql/types2";
import { ErrorRenderer, MutationForm } from "~/components/forms/MutationForm";

type FormData = {
  effectiveDate: Moment | null;
  rateSheetName: string | null;
};

type AssignProjectExpenseRateSheetProps = {
  project: Project;
};

const AssignProjectExpenseRateSheet: React.FC<
  AssignProjectExpenseRateSheetProps
> = ({ project }) => {
  const defaultFormValues: FormData = {
    effectiveDate: null,
    rateSheetName: null,
  };

  useBreadcrumbs(
    [
      { text: "Customers", to: customerManagement.path },
      {
        text: `Customer ${project?.customer?.number}: ${project?.customer?.name}`,
        to: customerRoute.toRoute(project?.customer?.number!).path,
      },
      {
        text: `Project ${project?.number}: ${project?.name}`,
        to: projectRoute.toRoute(project?.number).path,
      },
      { text: "Assign Expense Rate sheet" },
    ],
    [project]
  );

  const [doMutation, { loading }] =
    useAssignExpenseRateSheetToProjectMutation();

  const reloadProject = useReloadProject();
  const navigate = useNavigate();

  const onSuccess = () => {
    reloadProject();
    navigate(projectRoute.toRoute(project?.number).path);
  };

  const runMutation = async (values: FormData) => {
    const variables = {
      effectiveDate: values?.effectiveDate?.format("YYYY-MM-DD")!,
      rateSheetName: values?.rateSheetName!,
      projectNumbers: [project?.number]!,
    };

    const res = await doMutation({ variables });

    return res?.data?.expenseRates
      ?.assignRateSheetToProject as ValidationResponse;
  };

  return (
    <>
      <Spinner open={loading} />
      <MutationForm
        onSuccess={onSuccess}
        initialValues={defaultFormValues}
        runMutation={runMutation}
      >
        <ErrorRenderer
          render={(error) => (
            <ValueRenderer
              render={(values) => (
                <AssignRateSheet
                  projectNumber={project?.number}
                  errorMessage={error}
                  loading={loading}
                  type="Expense"
                  effectiveDate={values.effectiveDate}
                />
              )}
            />
          )}
        />
      </MutationForm>
    </>
  );
};

export default AssignProjectExpenseRateSheet;
